import React, { useState, useEffect, useMemo } from "react"
import { Button, Select, Spin, Tag } from "antd"
import { CloseOutlined, PlusOutlined, LoadingOutlined } from "@ant-design/icons"
import { Text } from "ui/Typography"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import {
  getAssignedCrew,
  getAvailableStaff,
  assignStaffMember,
  unassignStaffMember,
  selectAssignedCrew,
  selectAssignedCrewLoading,
  selectAvailableStaff,
  selectAvailableStaffLoading
} from "store/staffAssignments"
import { getRoles, selectBookingAssignableRoles } from "store/staff"
import styles from "./index.module.css"

const CrewTab = ({ booking }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [selectedRoles, setSelectedRoles] = useState([])
  const [showAvailableCrew, setShowAvailableCrew] = useState(false)
  const [assigningStaffId, setAssigningStaffId] = useState(null)
  const [unassigningCrewId, setUnassigningCrewId] = useState(null)

  const assignedCrew = useSelector(selectAssignedCrew)
  const assignedCrewLoading = useSelector(selectAssignedCrewLoading)
  const availableStaff = useSelector(selectAvailableStaff)
  const availableStaffLoading = useSelector(selectAvailableStaffLoading)
  const bookingRoles = useSelector(selectBookingAssignableRoles)

  const bookingId = booking?.id

  useEffect(() => {
    if (bookingId) {
      dispatch(getAssignedCrew(bookingId))
      dispatch(getAvailableStaff(bookingId))
    }
  }, [dispatch, bookingId])

  useEffect(() => {
    dispatch(getRoles())
  }, [dispatch])

  const experienceRoles = useMemo(() => {
    return bookingRoles?.map((role) => role?.name)
  }, [bookingRoles])

  const getStaffPrimaryRole = (staff) => {
    if (staff?.roles && staff?.roles?.length > 0) {
      return staff.roles[0].name
    }
    return "N/A"
  }

  const handleUnassign = async (assignmentId) => {
    if (!assignmentId) return

    setUnassigningCrewId(assignmentId)
    try {
      await dispatch(unassignStaffMember(assignmentId))
      if (bookingId) {
        dispatch(getAssignedCrew(bookingId))
        dispatch(getAvailableStaff(bookingId))
      }
    } catch (error) {
      console.error("Failed to unassign crew member:", error)
    } finally {
      setUnassigningCrewId(null)
    }
  }

  const handleAssign = async (staffMember) => {
    if (!bookingId || !staffMember) return

    const primaryRole =
      staffMember.roles && staffMember.roles.length > 0 ? staffMember.roles[0].name.toLowerCase().replace(/\s+/g, "_") : "crew_member"

    setAssigningStaffId(staffMember.id)
    try {
      await dispatch(
        assignStaffMember(bookingId, {
          profile_id: staffMember.id,
          role: primaryRole
        })
      )
      if (bookingId) {
        dispatch(getAssignedCrew(bookingId))
        dispatch(getAvailableStaff(bookingId))
      }
    } catch (error) {
      console.error("Failed to assign crew member:", error)
    } finally {
      setAssigningStaffId(null)
    }
  }

  const handleRoleFilter = (values) => {
    setSelectedRoles(values)
  }

  const filteredAvailableCrew = useMemo(() => {
    if (selectedRoles.length === 0) return availableStaff

    return availableStaff.filter((staff) => {
      return staff.roles.some((role) => selectedRoles.includes(role.name))
    })
  }, [availableStaff, selectedRoles])

  return (
    <div className={styles.crewTabContainer}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Text className={styles.sectionTitle}>
            {t("booking.crew.assigned_crew")} ({assignedCrew.length})
          </Text>
        </div>
        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <div className={styles.crewMemberColumn}>
              <Text className={styles.headerText}>{t("booking.crew.crew_member")}</Text>
            </div>
            <div className={styles.roleColumn}>
              <Text className={styles.headerText}>{t("booking.crew.role")}</Text>
            </div>
            <div className={styles.actionColumn}>
              <Text className={styles.headerText}>{t("booking.crew.unassign")}</Text>
            </div>
          </div>
          {assignedCrewLoading ? (
            <div className={styles.centeredRow}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />} />
              <Text style={{ marginLeft: 12 }}>{t("booking.crew.loading")}</Text>
            </div>
          ) : assignedCrew.length === 0 ? (
            <div className={styles.centeredRow}>
              <Text>{t("booking.crew.no_available_crew")}</Text>
            </div>
          ) : (
            assignedCrew?.map((crew) => (
              <div key={crew.id} className={styles.tableRow}>
                <div className={styles.crewMemberColumn}>
                  <Text>{crew.profileName}</Text>
                </div>
                <div className={styles.roleColumn}>
                  <Text>{crew.assignmentRole}</Text>
                </div>
                <div className={styles.actionColumn}>
                  <button
                    className={styles.unassignButton}
                    onClick={() => handleUnassign(crew.id)}
                    disabled={unassigningCrewId === crew.id}
                  >
                    {unassigningCrewId === crew.id ? <LoadingOutlined style={{ fontSize: 16 }} spin /> : <CloseOutlined />}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
        <Button type="link" icon={<PlusOutlined />} className={styles.addButton} onClick={() => setShowAvailableCrew(!showAvailableCrew)}>
          {t("booking.crew.add_crew_member")}
        </Button>
      </div>
      {showAvailableCrew && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Text className={styles.sectionTitle}>
              {t("booking.crew.available_crew")} ({filteredAvailableCrew.length})
            </Text>
          </div>
          <div className={styles.filterContainer}>
            <Select
              mode="multiple"
              placeholder={`${t("booking.crew.roles_placeholder")} (${experienceRoles.length})`}
              value={selectedRoles}
              onChange={handleRoleFilter}
              className={styles.roleFilter}
              style={{ minWidth: 200 }}
              disabled={availableStaffLoading || experienceRoles.length === 0}
            >
              {experienceRoles?.map((roleName) => (
                <Select.Option key={roleName} value={roleName}>
                  {roleName}
                </Select.Option>
              ))}
            </Select>
            {selectedRoles.length > 0 && (
              <div className={styles.roleTagsContainer}>
                {selectedRoles.map((role) => (
                  <Tag
                    key={role}
                    closable
                    onClose={() => setSelectedRoles(selectedRoles.filter((r) => r !== role))}
                    className={styles.roleTag}
                  >
                    {role}
                  </Tag>
                ))}
              </div>
            )}
          </div>
          <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
              <div className={styles.crewMemberColumn}>
                <Text className={styles.headerText}>{t("booking.crew.crew_member")}</Text>
              </div>
              <div className={styles.roleColumn}>
                <Text className={styles.headerText}>{t("booking.crew.role")}</Text>
              </div>
              <div className={styles.actionColumn}>
                <Text className={styles.headerText}>{t("booking.crew.assign")}</Text>
              </div>
            </div>
            {availableStaffLoading ? (
              <div className={styles.centeredRow}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />} />
                <Text style={{ marginLeft: 12 }}>{t("booking.crew.loading")}</Text>
              </div>
            ) : filteredAvailableCrew?.length === 0 ? (
              <div className={styles.centeredRow}>
                <Text>{t("booking.crew.no_available_crew")}</Text>
              </div>
            ) : (
              filteredAvailableCrew?.map((crew) => (
                <div key={crew.id} className={styles.tableRow}>
                  <div className={styles.crewMemberColumn}>
                    <Text>{crew.fullName}</Text>
                  </div>
                  <div className={styles.roleColumn}>
                    <Text>{getStaffPrimaryRole(crew)}</Text>
                  </div>
                  <div className={styles.actionColumn}>
                    <button className={styles.assignButton} onClick={() => handleAssign(crew)} disabled={assigningStaffId === crew.id}>
                      {assigningStaffId === crew.id ? <LoadingOutlined style={{ fontSize: 16 }} spin /> : <PlusOutlined />}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

CrewTab.propTypes = {
  booking: PropTypes.object
}

export default CrewTab
